import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Table, { TableProps } from 'further-ui/components/Table';
import { useSyncScroll } from 'contexts/SyncScrollContext';

type StyledTableProps = {
  externalScroll?: boolean;
  withIndentation?: boolean;
  headerRowFontSize?: number;
  headerRowLineHeight?: number;
  headerRowPaddingY?: number;
  bodyRowFontSize?: number;
  bodyRowLineHeight?: number;
  bodyRowPaddingY?: number;
};

const shouldForwardProp = (prop: string) =>
  ![
    'externalScroll',
    'withIndentation',
    'headerRowFontSize',
    'headerRowLineHeight',
    'headerRowPaddingY',
    'bodyRowFontSize',
    'bodyRowLineHeight',
    'bodyRowPaddingY',
  ].includes(prop);

const StyledTableWrapper = styled(Box, {
  shouldForwardProp,
})<StyledTableProps>(
  ({
    theme,
    externalScroll,
    withIndentation,
    headerRowFontSize,
    headerRowLineHeight,
    headerRowPaddingY,
    bodyRowFontSize,
    bodyRowLineHeight,
    bodyRowPaddingY,
  }) => ({
    ...(externalScroll && {
      '& .MuiTableContainer-root': {
        overflow: 'visible',
      },
    }),
    ...(withIndentation && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:first-of-type':
        { paddingLeft: theme.spacing(theme.spacingSizes.lg) },
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root:not(.table-custom-footer-row) > .MuiTableCell-root:first-of-type':
        { paddingLeft: theme.spacing(theme.spacingSizes.lg) },
    }),
    ...((headerRowFontSize !== undefined ||
      headerRowLineHeight !== undefined ||
      headerRowPaddingY !== undefined) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root':
        {
          ...(headerRowFontSize !== undefined && {
            fontSize: `${headerRowFontSize}px`,
          }),
          ...(headerRowLineHeight !== undefined && {
            lineHeight: `${headerRowLineHeight}px`,
          }),
          ...(headerRowPaddingY !== undefined && {
            paddingTop: `${headerRowPaddingY}px`,
            paddingBottom: `${headerRowPaddingY}px`,
          }),
        },
    }),
    ...((headerRowFontSize !== undefined ||
      headerRowLineHeight !== undefined) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root > *':
        {
          ...(headerRowFontSize !== undefined && {
            fontSize: `${headerRowFontSize}px`,
          }),
          ...(headerRowLineHeight !== undefined && {
            lineHeight: `${headerRowLineHeight}px`,
          }),
        },
    }),
    ...((bodyRowFontSize !== undefined ||
      bodyRowLineHeight !== undefined ||
      bodyRowPaddingY !== undefined) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root':
        {
          ...(bodyRowFontSize !== undefined && {
            fontSize: `${bodyRowFontSize}px`,
          }),
          ...(bodyRowLineHeight !== undefined && {
            lineHeight: `${bodyRowLineHeight}px`,
          }),
          ...(bodyRowPaddingY !== undefined && {
            paddingTop: `${bodyRowPaddingY}px`,
            paddingBottom: `${bodyRowPaddingY}px`,
          }),
        },
    }),
    ...((bodyRowFontSize !== undefined || bodyRowLineHeight !== undefined) && {
      '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root > *':
        {
          ...(bodyRowFontSize !== undefined && {
            fontSize: `${bodyRowFontSize}px`,
          }),
          ...(bodyRowLineHeight !== undefined && {
            lineHeight: `${bodyRowLineHeight}px`,
          }),
        },
    }),
  }),
);

const AccountingTableWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'minWidth',
})<{ minWidth: number }>(({ minWidth }) => ({
  overflowX: 'auto',
  overflowY: 'hidden',
  minWidth: 0,
  '& > .MuiBox-root > .MuiTableContainer-root > .MuiTable-root': {
    minWidth,
  },
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

type Props<T extends { id: string }> = TableProps<T, string> & StyledTableProps;

const AccountingTable = <T extends { id: string }>({
  externalScroll = true,
  withIndentation = true,
  headerRowFontSize = 12,
  headerRowLineHeight = 16,
  headerRowPaddingY = 12,
  bodyRowFontSize = 13,
  bodyRowLineHeight = 20,
  bodyRowPaddingY = 10,
  ...tableProps
}: Props<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { maxScrollWidth, registerScrollContainer, syncScroll } =
    useSyncScroll();

  useEffect(() => {
    return registerScrollContainer(scrollRef);
  }, [registerScrollContainer]);

  const styledTable = (
    <StyledTableWrapper
      externalScroll={externalScroll}
      withIndentation={withIndentation}
      headerRowFontSize={headerRowFontSize}
      headerRowLineHeight={headerRowLineHeight}
      headerRowPaddingY={headerRowPaddingY}
      bodyRowFontSize={bodyRowFontSize}
      bodyRowLineHeight={bodyRowLineHeight}
      bodyRowPaddingY={bodyRowPaddingY}
    >
      <Table {...tableProps} />
    </StyledTableWrapper>
  );

  return externalScroll ? (
    <AccountingTableWrapper
      minWidth={maxScrollWidth}
      ref={scrollRef}
      onScroll={(e) => syncScroll(e.currentTarget.scrollLeft, scrollRef)}
    >
      {styledTable}
    </AccountingTableWrapper>
  ) : (
    styledTable
  );
};

export default AccountingTable;
